import { ReactNode } from 'react';

export interface MicrobiomeChart {
  /** Used for the mobile species toggle when a slide has two charts */
  name: string;
  /** Lottie file in public/lottie/cobiotics, without extension */
  animation: string;
  /** Native Lottie canvas size in px */
  width: number;
  height: number;
  /** Read out in the chart's accessible name */
  finding: string;
}

export interface MicrobiomeSlide {
  direction: 'increase' | 'decrease';
  title: ReactNode;
  description: ReactNode;
  /** Two charts render side by side on desktop and behind a toggle on mobile */
  charts: MicrobiomeChart[];
  figure: string;
  caption: ReactNode;
}

export interface MicrobiomeModuleContent {
  productName: string;
  title: ReactNode;
  subtitle: ReactNode;
  slides: MicrobiomeSlide[];
}

export const DM02_MICROBIOME: MicrobiomeModuleContent = {
  productName: 'DM-02',
  title:
    'Your gut microbiome shapes how the body produces, metabolizes, and absorbs nutrients.',
  subtitle: (
    <>
      In a pre-clinical study, DM-02<sup>®</sup> was shown to:<sup>†</sup>
    </>
  ),
  slides: [
    {
      direction: 'increase',
      title: (
        <>
          Increase levels of B Vitamin-producing gut microbes<sup>°</sup>
        </>
      ),
      description:
        'Microbes produce vitamins that humans can’t produce on their own.',
      charts: [
        {
          name: 'Oscillibacter',
          animation: 'oscillibacter-compare',
          width: 240,
          height: 250,
          finding: '7-fold change',
        },
        {
          name: 'Lawsonibacter',
          animation: 'lawsonibacter-compare',
          width: 240,
          height: 250,
          finding: '5-fold change',
        },
      ],
      figure: 'FIG. 01',
      caption: (
        <>
          DM-02<sup>®</sup> increased two native bacterial groups associated
          with vitamin B production after a GI simulation.<sup>°†</sup>
        </>
      ),
    },
    {
      direction: 'decrease',
      title: (
        <>
          Lower colonic pH<sup>°</sup>
        </>
      ),
      description:
        'Lower colonic ph aids in absorption of minerals and electrolytes.',
      charts: [
        {
          name: 'pH',
          animation: 'ph-compare',
          width: 240,
          height: 250,
          finding: '3% decrease',
        },
      ],
      figure: 'FIG. 02',
      caption: (
        <>
          DM-02<sup>®</sup> reduced colonic pH across donor microbiomes after a
          GI simulation.<sup>†</sup>
        </>
      ),
    },
    {
      direction: 'increase',
      title: (
        <>
          Increase beneficial short-chain fatty acids<sup>°</sup>
        </>
      ),
      description:
        'Produced by beneficial bacteria to support the gut, brain, and immune system.',
      charts: [
        {
          name: 'SCFA',
          animation: 'scfa-compare',
          width: 240,
          height: 250,
          finding: '9% increase',
        },
      ],
      figure: 'FIG. 03',
      caption: (
        <>
          DM-02<sup>®</sup> increased total SCFA production across donor
          microbiomes after a GI simulation.<sup>†</sup>
        </>
      ),
    },
  ],
};

export const AM02_MICROBIOME: MicrobiomeModuleContent = {
  productName: 'AM-02',
  title:
    'Your gut microbiome shapes how you feel, think, and perform every day.',
  subtitle: (
    <>
      In a pre-clinical study, AM-02<sup>®</sup> was shown to:<sup>†</sup>
    </>
  ),
  slides: [
    {
      direction: 'increase',
      title: (
        <>
          Increase acetate production<sup>°</sup>
        </>
      ),
      description:
        'A beneficial compound produced by gut bacteria that acts through the gut-brain axis and can impact brain health.',
      charts: [
        {
          name: 'Acetate',
          animation: 'acetate-compare',
          width: 240,
          height: 250,
          finding: '12% increase',
        },
      ],
      figure: 'FIG. 01',
      caption: (
        <>
          AM-02<sup>®</sup> increased acetate levels across healthy donor
          microbiomes after a GI simulation.<sup>†</sup>
        </>
      ),
    },
  ],
};

export const PM02_MICROBIOME: MicrobiomeModuleContent = {
  productName: 'PM-02',
  title:
    'Your gut microbiome is closely linked to the body’s biological clock.',
  subtitle: (
    <>
      In a pre-clinical study, PM-02<sup>®</sup> was shown to:
    </>
  ),
  slides: [
    {
      direction: 'increase',
      title: (
        <>
          Increase butyrate<sup>°†</sup>
        </>
      ),
      description:
        'A short-chain fatty acid made by gut bacteria that may play a role in circadian-rhythm regulation.',
      charts: [
        {
          name: 'Butyrate',
          animation: 'butyrate-compare',
          width: 240,
          height: 250,
          finding: '20% increase',
        },
      ],
      figure: 'FIG. 01',
      caption: (
        <>
          PM-02<sup>®</sup> increased butyrate levels across healthy donor
          microbiomes after a GI simulation.<sup>†</sup>
        </>
      ),
    },
    {
      direction: 'increase',
      title: (
        <>
          Increase tryptophan availability<sup>°†</sup>
        </>
      ),
      description:
        'An essential amino acid substrate in the production of natural melatonin.',
      charts: [
        {
          name: 'Tryptophan',
          animation: 'tryptophan-compare',
          width: 260,
          height: 250,
          finding: '48% increase',
        },
      ],
      figure: 'FIG. 02',
      caption: (
        <>
          PM-02<sup>®</sup> increased average tryptophan abundance across
          healthy donor microbiomes after a GI simulation.<sup>†</sup>
        </>
      ),
    },
  ],
};
